import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { movies, genres, years } from "@/mocks/movies";
import type { Movie } from "@/types/movie";
import { searchMovies } from "@/lib/movieList";
import MovieCard from "@/components/feature/MovieCard";

const list = movies as Movie[];

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") ?? "";
  const [input, setInput] = useState(query);
  const [genre, setGenre] = useState("全部");
  const [year, setYear] = useState<number | "all">("all");

  useEffect(() => {
    setInput(query);
    setGenre("全部");
    setYear("all");
  }, [query]);

  useEffect(() => {
    document.title = query ? `“${query}”搜索结果 - 97影院` : "搜索影视 - 97影院";
  }, [query]);

  const results = useMemo(() => {
    let base = query ? searchMovies(list, query) : list;
    if (genre !== "全部") base = base.filter((m) => m.genre === genre);
    if (year !== "all") base = base.filter((m) => m.year === year);
    return base;
  }, [query, genre, year]);

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    const q = input.trim();
    setSearchParams(q ? { q } : {});
  };

  return (
    <div className="mx-auto w-full max-w-[1440px] px-4 py-8 md:px-8 md:py-10">
      <nav className="mb-5 flex items-center gap-1.5 text-sm text-foreground-500">
        <Link to="/" className="transition-colors hover:text-primary-600">首页</Link>
        <i className="ri-arrow-right-s-line" />
        <span className="text-foreground-900">搜索结果</span>
      </nav>

      <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-7">
        <h1 className="font-heading text-2xl font-black text-foreground-950 md:text-3xl">搜索影视</h1>
        <form onSubmit={handleSubmit} className="relative mt-5">
          <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-lg text-foreground-400" />
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="输入影视名称、类型或主演，如：科幻、狂飙"
            className="h-12 w-full rounded-md border border-background-300 bg-background-100 pl-11 pr-28 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
          />
          {input && (
            <button
              type="button"
              onClick={() => setInput("")}
              className="absolute right-24 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full text-foreground-400 hover:bg-background-200"
              aria-label="清空"
            >
              <i className="ri-close-line text-lg" />
            </button>
          )}
          <button
            type="submit"
            className="absolute right-2 top-1/2 h-9 -translate-y-1/2 whitespace-nowrap rounded-md bg-primary-500 px-5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            搜索
          </button>
        </form>

        {query && (
          <p className="mt-4 text-sm text-foreground-600">
            当前检索词：<span className="font-semibold text-accent-600">{query}</span> · 共找到{" "}
            <span className="font-semibold text-primary-700">{results.length}</span> 条结果
          </p>
        )}
      </div>

      <div className="mt-8 flex flex-col gap-6 lg:flex-row lg:gap-8">
        <aside className="w-full shrink-0 lg:w-64">
          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h3 className="flex items-center gap-2 text-sm font-bold text-foreground-950">
              <i className="ri-equalizer-line text-base text-primary-600" />
              简化筛选
            </h3>
            <div className="mt-4">
              <p className="mb-2 text-xs font-semibold text-foreground-700">类型题材</p>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setGenre("全部")}
                  className={`rounded-full border px-3 py-1 text-xs transition-colors ${
                    genre === "全部" ? "border-primary-500 bg-primary-500 text-background-50" : "border-background-300 text-foreground-600"
                  }`}
                >
                  全部
                </button>
                {genres.map((g) => (
                  <button
                    key={g}
                    type="button"
                    onClick={() => setGenre(g)}
                    className={`rounded-full border px-3 py-1 text-xs transition-colors ${
                      genre === g ? "border-primary-500 bg-primary-500 text-background-50" : "border-background-300 text-foreground-600"
                    }`}
                  >
                    {g}
                  </button>
                ))}
              </div>
            </div>
            <div className="mt-5">
              <p className="mb-2 text-xs font-semibold text-foreground-700">上映年份</p>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setYear("all")}
                  className={`rounded-full border px-3 py-1 text-xs transition-colors ${
                    year === "all" ? "border-primary-500 bg-primary-500 text-background-50" : "border-background-300 text-foreground-600"
                  }`}
                >
                  全部
                </button>
                {years.map((y) => (
                  <button
                    key={y}
                    type="button"
                    onClick={() => setYear(y)}
                    className={`rounded-full border px-3 py-1 text-xs transition-colors ${
                      year === y ? "border-primary-500 bg-primary-500 text-background-50" : "border-background-300 text-foreground-600"
                    }`}
                  >
                    {y}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </aside>

        <div className="min-w-0 flex-1">
          {results.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 py-20 text-center">
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100">
                <i className="ri-search-eye-line text-3xl text-foreground-400" />
              </span>
              <p className="mt-4 text-base font-medium text-foreground-800">没有找到相关影视作品</p>
              <p className="mt-1 text-sm text-foreground-500">换个关键词试试，或浏览热门推荐</p>
              <div className="mt-6 flex flex-wrap justify-center gap-3">
                <Link
                  to="/category/movie"
                  className="whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                >
                  浏览电影库
                </Link>
                <Link
                  to="/category/tv"
                  className="whitespace-nowrap rounded-md border border-primary-200 bg-primary-50 px-5 py-2.5 text-sm font-semibold text-primary-700 transition-colors hover:bg-primary-100"
                >
                  浏览电视剧库
                </Link>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 xl:grid-cols-5 md:gap-5">
              {results.map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}